// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — per-entity GEOMETRY projector (T1, split from captureSnapshot)
// ═══════════════════════════════════════════════════════════════════════════
//
// `toEntityGeometry(entity): SnapshotEntityGeometry` — maps one typed `DrawingEntity` onto the
// discriminated `SnapshotEntityGeometry` facet `captureSnapshot`'s `toEntityRef` attaches under the
// `geometry` field (projected by the `entities` route ONLY when `?fields=geometry` is requested —
// non-lean opt-in). Extracted into its own module so `captureSnapshot.ts` stays within the file-size
// budget (the 17-arm switch + the per-type field copies are ~250 lines on their own).
//
// PER-TYPE FIDELITY (ПРИОРИТЕТ #1 — никаких упрощений / inference):
//   • Every geometry field is the typed entity's OWN field, copied VERBATIM — no quantization, no
//     rounding, no derivation, no flattening of a structured point. The coordinate the agent reads
//     is the exact coordinate the model carries (`feedback-roundtrip-preserve-source-never-infer`).
//   • A REQUIRED schema field (a `.default(...)`/required point) is always copied; an OPTIONAL schema
//     field (`.optional()`) is attached ONLY when present on the source (mirroring the metadata
//     facet's optional-vs-present contract — an absent optional is OMITTED, not synthesized).
//   • The heavy arms (Spline control points, LWPOLYLINE/REVCLOUD vertices, Hatch boundary edges +
//     custom PAT lines + seed points, Multileader leader-line vertices + break segments) project their
//     FULL defining geometry — NOT a sampled polyline / one-control-point / single-leader summary (the
//     ПРИОРИТЕТ #1 forbidden shortcuts). They are byte-gated by the `?fields=geometry` opt-in +
//     `?fullField=geometry` uncapped single-item retrieval, never silently truncated.
//
// DELIBERATE NON-GEOMETRY EXCLUSIONS (NOT misses — the agent's geometry read-model, not the round-trip
// writer's full field set): the MLEADER structural CONTEXT_DATA mirror (`contextData`/`secondaryContexts`
// — a deeply-nested handle/raw-laden graph that PARALLELS the projected `leaderRoots` leader geometry),
// the ARRAYRECT AcDbAssocArray graph (evaluated `itemTransforms` / persisted `assocItems` / driving
// expression strings / handles), the Viewport rich view-state (on the dedicated `viewports` route), and
// every entity's appearance/style metadata (annotativeContexts, gradient/background fill, raw passthrough
// group bags, handle linkages). These live on the full typed model; this facet is the DEFINING shape.
//
// BY-CONSTRUCTION COMPLETENESS: the switch covers every `drawingEntitySchema` arm (the discriminant
// is the same `type` literal); the `default` branch is an `assertNever` so a NEW entity type added to
// `drawingEntitySchema` without a geometry arm here is a tsc COMPILE ERROR — the `entities` route
// can never silently drop a type's geometry.
//
// COMPACT-STORE / STREAMED FIDELITY: `captureSnapshot` walks `space.entities`, whose read-through
// getter re-materializes each entity from the compact cache (`entityFromCompactStore`) for a streamed
// import. The compact store reconstructs the FULL typed geometry (the simple-geometry / polyline /
// hatch column packers round-trip start/end/center/radius/vertices/boundaryPaths), so `toEntityGeometry`
// reads the SAME defining geometry whether the entity is inline or compact-backed. (The few optional
// METADATA fields the compact store drops — `linetypeScale`, viewport `frozenLayerHandles` — are not
// geometry; they ride the metadata facet, documented in captureSnapshot's compact-store note.)
//
// LEAF / DEP-GRAPH (ПРИОРИТЕТ #2): imports ONLY the drawing-model entity TYPES (`import type`, erases
// at compile time) + the seam geometry TYPES (`import type`). NO editorStore / agentBridge / runtime
// drawing-model import → no new dep-graph edge, `npm run deps:graph` cycles=0 (the same store-free
// posture `captureSnapshot` keeps for the facet TYPES).
// ═══════════════════════════════════════════════════════════════════════════

import {
  polylineIsClosed,
  polylineSubtype,
  tableRotationDegrees,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  GEOMETRY_PER_ENTITY_BYTE_CAP,
  type GeomMleaderBreakPoint,
  type GeomPoint3D,
  type GeomPointOptionalZ,
  type GeomPolylineVertex,
  type SnapshotEntityGeometry,
} from "../ai/sceneSummaryProjector";

/**
 * Copy a `drawingPointOptionalZSchema` point ({x,y,z?}) verbatim — `z` attached ONLY when the source
 * carried it (a bare 2D `{x,y}` literal omits it; never synthesized to 0, mirroring the schema's
 * `z?: number` — the round-trip writer emits `z ?? 0`, but the READ model reports the true absence).
 */
function pointOptionalZ(p: { x: number; y: number; z?: number }): GeomPointOptionalZ {
  return p.z !== undefined ? { x: p.x, y: p.y, z: p.z } : { x: p.x, y: p.y };
}

/** Copy a `drawingPoint3DSchema` point ({x,y,z}) verbatim — `z` is required (a `.default(0)`). */
function point3D(p: { x: number; y: number; z: number }): GeomPoint3D {
  return { x: p.x, y: p.y, z: p.z };
}

/** Copy one `polylineVertexSchema` vertex verbatim (x/y + bulge + per-vertex widths + optional 91 id). */
function polylineVertex(v: {
  x: number;
  y: number;
  bulge: number;
  startWidth: number;
  endWidth: number;
  vertexId?: number;
}): GeomPolylineVertex {
  const out: { -readonly [K in keyof GeomPolylineVertex]?: GeomPolylineVertex[K] } = {
    x: v.x,
    y: v.y,
    bulge: v.bulge,
    startWidth: v.startWidth,
    endWidth: v.endWidth,
  };
  if (v.vertexId !== undefined) out.vertexId = v.vertexId;
  return out as GeomPolylineVertex;
}

/**
 * Copy one `mleaderBreakPointSchema` break segment verbatim — a `{start,end}` optional-z pair marking
 * where a leader line is broken; `breakIndex` (group 90) attached ONLY when present (a LeaderLine break
 * carries it; a LeaderData break omits it).
 */
function mleaderBreakPoint(b: {
  start: { x: number; y: number; z?: number };
  end: { x: number; y: number; z?: number };
  breakIndex?: number;
}): GeomMleaderBreakPoint {
  const out: { -readonly [K in keyof GeomMleaderBreakPoint]?: GeomMleaderBreakPoint[K] } = {
    start: pointOptionalZ(b.start),
    end: pointOptionalZ(b.end),
  };
  if (b.breakIndex !== undefined) out.breakIndex = b.breakIndex;
  return out as GeomMleaderBreakPoint;
}

/**
 * Project one typed `DrawingEntity` onto its per-type `SnapshotEntityGeometry` arm (verbatim, full
 * fidelity, UNCAPPED). The `default` branch is an `assertNever` — a new `drawingEntitySchema` arm
 * without a geometry case here is a tsc compile error (by-construction completeness). The PUBLIC
 * entry point `toEntityGeometry` wraps this with the per-entity byte cap (`capGeometry`).
 */
function projectGeometry(entity: DrawingEntity): SnapshotEntityGeometry {
  switch (entity.type) {
    case "Line": {
      const g: SnapshotEntityGeometry = {
        kind: "Line",
        start: pointOptionalZ(entity.start),
        end: pointOptionalZ(entity.end),
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
      return g;
    }
    case "Point":
      return {
        kind: "Point",
        location: pointOptionalZ(entity.location),
        rotation: entity.rotation,
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Circle":
      return {
        kind: "Circle",
        center: pointOptionalZ(entity.center),
        radius: entity.radius,
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Arc":
      return {
        kind: "Arc",
        center: pointOptionalZ(entity.center),
        radius: entity.radius,
        startAngle: entity.startAngle,
        endAngle: entity.endAngle,
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Ellipse":
      return {
        kind: "Ellipse",
        // SL-15 — project the real center-z (DXF 30) + major-axis-z (31) via the
        // optional-z helper (omitted when the source omitted it, never →0).
        center: pointOptionalZ(entity.center),
        majorAxisEndpoint: pointOptionalZ(entity.majorAxisEndpoint),
        ratio: entity.ratio,
        startParam: entity.startParam,
        endParam: entity.endParam,
        extrusion: point3D(entity.extrusion),
        thickness: entity.thickness,
      };
    case "Spline":
      return {
        kind: "Spline",
        degree: entity.degree,
        closed: entity.closed,
        periodic: entity.periodic,
        rational: entity.rational,
        planar: entity.planar,
        ...(entity.linear !== undefined ? { linear: entity.linear } : {}),
        fitPoints: entity.fitPoints.map(point3D),
        ...(entity.startTangent !== undefined ? { startTangent: point3D(entity.startTangent) } : {}),
        ...(entity.endTangent !== undefined ? { endTangent: point3D(entity.endTangent) } : {}),
        controlPoints: entity.controlPoints.map(point3D),
        knots: [...entity.knots],
        weights: [...entity.weights],
        knotTolerance: entity.knotTolerance,
        controlPointTolerance: entity.controlPointTolerance,
        fitTolerance: entity.fitTolerance,
        // SL-15 — the SPLINE normal (DXF 210/220/230), attached only when present.
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "LWPOLYLINE":
      return {
        kind: "LWPOLYLINE",
        vertices: entity.vertices.map(polylineVertex),
        closed: entity.closed,
        ...(entity.plinegen !== undefined ? { plinegen: entity.plinegen } : {}),
        constantWidth: entity.constantWidth,
        elevation: entity.elevation,
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Text":
      return {
        kind: "Text",
        insertionPoint: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        ...(entity.alignmentPoint !== undefined
          ? { alignmentPoint: { x: entity.alignmentPoint.x, y: entity.alignmentPoint.y } }
          : {}),
        height: entity.height,
        rotation: entity.rotation,
        contents: entity.contents,
        styleName: entity.styleName,
        widthFactor: entity.widthFactor,
        obliqueAngle: entity.obliqueAngle,
        hJustification: entity.hJustification,
        vJustification: entity.vJustification,
        thickness: entity.thickness,
        generationFlags: entity.generationFlags,
        extrusion: point3D(entity.extrusion),
      };
    case "MText":
      return {
        kind: "MText",
        insertionPoint: { x: entity.insertionPoint.x, y: entity.insertionPoint.y },
        nominalHeight: entity.nominalHeight,
        referenceWidth: entity.referenceWidth,
        attachmentPoint: entity.attachmentPoint,
        drawingDirection: entity.drawingDirection,
        contents: entity.contents,
        styleName: entity.styleName,
        rotation: entity.rotation,
        lineSpacingStyle: entity.lineSpacingStyle,
        lineSpacingFactor: entity.lineSpacingFactor,
        xAxisDirection: point3D(entity.xAxisDirection),
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
        // The resolved text-box EXTENTS + the COLUMN layout that defines multi-column placement
        // (geometry, all `.default(...)` → always present). The background-fill APPEARANCE scalars
        // (backgroundFill / fillBoxScale / colors / transparency) stay off this facet (render metadata).
        horizontalWidth: entity.horizontalWidth,
        verticalHeight: entity.verticalHeight,
        columnType: entity.columnType,
        columnCount: entity.columnCount,
        columnWidth: entity.columnWidth,
        columnGutter: entity.columnGutter,
        columnHeights: [...entity.columnHeights],
        columnFlowReversed: entity.columnFlowReversed,
        columnAutoheight: entity.columnAutoheight,
      };
    case "Dimension":
      return {
        kind: "Dimension",
        dimensionType: entity.dimensionType,
        defPoint: { x: entity.defPoint.x, y: entity.defPoint.y },
        textMidpoint: { x: entity.textMidpoint.x, y: entity.textMidpoint.y },
        dimensionText: entity.dimensionText,
        measurement: entity.measurement,
        ...(entity.extLine1Point !== undefined
          ? { extLine1Point: { x: entity.extLine1Point.x, y: entity.extLine1Point.y } }
          : {}),
        ...(entity.extLine2Point !== undefined
          ? { extLine2Point: { x: entity.extLine2Point.x, y: entity.extLine2Point.y } }
          : {}),
        ...(entity.thirdPoint !== undefined ? { thirdPoint: point3D(entity.thirdPoint) } : {}),
        ...(entity.arcPoint !== undefined ? { arcPoint: point3D(entity.arcPoint) } : {}),
        ...(entity.insertionPoint !== undefined
          ? { insertionPoint: point3D(entity.insertionPoint) }
          : {}),
        linearAngle: entity.linearAngle,
        textRotation: entity.textRotation,
        obliqueAngle: entity.obliqueAngle,
        // code 53 — text angle relative to the dimension line (geometry sibling of textRotation).
        textAngleRelativeToDimLine: entity.textAngleRelativeToDimLine,
        leaderLength: entity.leaderLength,
        // MIRRTEXT glyph-handedness (mirrtext-dimension-mleader-attrib) — render-only, OPTIONAL (no
        // DXF group; AcDbDimension has no dedicated handedness code, so it is never DXF-persisted).
        // Determines whether the dim text draws mirrored (backward), a geometry sibling of
        // textRotation/textAngleRelativeToDimLine — absence-preserving like the other optionals above.
        ...(entity.textMirrored !== undefined ? { textMirrored: entity.textMirrored } : {}),
        // Text-placement geometry (schema gate reveal): the dim TEXT size (140) + attachment (71) that
        // define where/how big the text sits; the OPTIONAL multi-line layout (72/41) mirrors MText.
        textHeight: entity.textHeight,
        attachmentPoint: entity.attachmentPoint,
        ...(entity.lineSpacingStyle !== undefined
          ? { lineSpacingStyle: entity.lineSpacingStyle }
          : {}),
        ...(entity.lineSpacingFactor !== undefined
          ? { lineSpacingFactor: entity.lineSpacingFactor }
          : {}),
        // DIMENSION is OCS-planar — the OPTIONAL extrusion (210/220/230) + thickness (39), verbatim.
        ...(entity.thickness !== undefined ? { thickness: entity.thickness } : {}),
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Multileader":
      // The defining LEADER geometry: root → node → line → vertices, PLUS the break-segment geometry
      // at BOTH the leader-line level (breakIndex-carrying) and the LeaderData node level, plus the
      // node dogleg vector/length + last-leader point. The structural CONTEXT_DATA mirror
      // (`contextData`/`secondaryContexts`) is DELIBERATELY OMITTED — it is the byte-faithful
      // round-trip writer's full field set (a deeply-nested handle/raw-laden graph), not the agent's
      // geometry read-model; `leaderRoots` IS the defining leader geometry (see the seam type doc).
      return {
        kind: "Multileader",
        textLocation: { x: entity.textLocation.x, y: entity.textLocation.y },
        ...(entity.basePoint !== undefined
          ? { basePoint: { x: entity.basePoint.x, y: entity.basePoint.y } }
          : {}),
        textContents: entity.textContents,
        textHeight: entity.textHeight,
        textRotation: entity.textRotation,
        // MIRRTEXT glyph-handedness (mirrtext-dimension-mleader-attrib) — render-only, OPTIONAL. DXF
        // home is the CONTEXT_DATA MTextData OCS extrusion Z-sign (dxfBridge writes/reads it there);
        // this is the decoded boolean the render adapter + MIRROR command consume, a geometry sibling
        // of textRotation — absence-preserving like blockScaleVector/blockConnectionType above.
        ...(entity.textMirrored !== undefined ? { textMirrored: entity.textMirrored } : {}),
        contentType: entity.contentType,
        // BLOCK-content geometry (finding :289) — for a `contentType === 1` block-content MLEADER the
        // drawn shape is a BLOCK placed by name + scale/rotation + the readable ATTRIB payload (the
        // analogue of a Block INSERT's geometry). All `.default(...)` save `blockConnectionType`
        // (absence-preserving optional); a text-content MLEADER carries the schema defaults verbatim.
        blockName: entity.blockName,
        blockScale: entity.blockScale,
        blockRotation: entity.blockRotation,
        // #432 r13 (@codex seam) — the AcDbMLeader body block_scale_vector
        // (10/20/30). Projected like the sibling block transform fields
        // (blockScale/blockRotation); absence-preserving (optional model field).
        ...(entity.blockScaleVector !== undefined
          ? { blockScaleVector: point3D(entity.blockScaleVector) }
          : {}),
        ...(entity.blockConnectionType !== undefined
          ? { blockConnectionType: entity.blockConnectionType }
          : {}),
        blockAttributeReferences: entity.blockAttributeReferences.map((a) => ({
          attDefHandle: a.attDefHandle,
          index: a.index,
          width: a.width,
          value: a.value,
        })),
        leaderRoots: entity.leaderRoots.map((root) => ({
          nodes: root.nodes.map((node) => ({
            ...(node.doglegVector !== undefined
              ? { doglegVector: point3D(node.doglegVector) }
              : {}),
            ...(node.doglegLength !== undefined ? { doglegLength: node.doglegLength } : {}),
            ...(node.lastLeaderPoint !== undefined
              ? { lastLeaderPoint: point3D(node.lastLeaderPoint) }
              : {}),
            ...(node.breakPoints !== undefined
              ? { breakPoints: node.breakPoints.map(mleaderBreakPoint) }
              : {}),
            lines: node.lines.map((ll) => ({
              vertices: ll.vertices.map(pointOptionalZ),
              // LeaderLine breaks default to `[]` (always present) — project verbatim.
              breakPoints: ll.breakPoints.map(mleaderBreakPoint),
            })),
          })),
        })),
      };
    case "Revision Cloud":
      return {
        kind: "Revision Cloud",
        vertices: entity.vertices.map(polylineVertex),
        closed: entity.closed,
        ...(entity.plinegen !== undefined ? { plinegen: entity.plinegen } : {}),
        arcChordLength: entity.arcChordLength,
        approximateArcLength: entity.approximateArcLength,
        revisionCloudStyle: entity.revisionCloudStyle,
        creationMode: entity.creationMode,
        constantWidth: entity.constantWidth,
        elevation: entity.elevation,
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Wipeout":
      return {
        kind: "Wipeout",
        vertices: entity.vertices.map((v) => ({ x: v.x, y: v.y })),
        frame: entity.frame,
        clipMode: entity.clipMode,
      };
    case "Hatch":
      return {
        kind: "Hatch",
        patternName: entity.patternName,
        solidFill: entity.solidFill,
        associative: entity.associative,
        patternAngle: entity.patternAngle,
        patternScale: entity.patternScale,
        elevation: entity.elevation,
        // The full WCS elevation POINT: `elevation` is Z (30); elevationX/Y (10/20, usually 0) are
        // OPTIONAL and complete it (omitted when the source omitted them — never synthesized to 0).
        ...(entity.elevationX !== undefined ? { elevationX: entity.elevationX } : {}),
        ...(entity.elevationY !== undefined ? { elevationY: entity.elevationY } : {}),
        boundaryPaths: entity.boundaryPaths.map((path) => ({
          pathTypeFlag: path.pathTypeFlag,
          // Associative source-boundary refs (finding :345) — the 330 handles of the source entities
          // that define this path's geometry; OPTIONAL (absent for a non-associative path).
          ...(path.sourceBoundaryHandles !== undefined
            ? { sourceBoundaryHandles: [...path.sourceBoundaryHandles] }
            : {}),
          edges: path.edges.map((edge) => {
            switch (edge.edgeType) {
              case "line":
                return {
                  edgeType: "line" as const,
                  start: { x: edge.start.x, y: edge.start.y },
                  end: { x: edge.end.x, y: edge.end.y },
                };
              case "arc":
                return {
                  edgeType: "arc" as const,
                  center: { x: edge.center.x, y: edge.center.y },
                  radius: edge.radius,
                  startAngle: edge.startAngle,
                  endAngle: edge.endAngle,
                  counterclockwise: edge.counterclockwise,
                };
              case "ellipticArc":
                return {
                  edgeType: "ellipticArc" as const,
                  center: { x: edge.center.x, y: edge.center.y },
                  majorAxisEnd: { x: edge.majorAxisEnd.x, y: edge.majorAxisEnd.y },
                  minorRatio: edge.minorRatio,
                  startAngle: edge.startAngle,
                  endAngle: edge.endAngle,
                  counterclockwise: edge.counterclockwise,
                };
              default: {
                const exhaustive: never = edge;
                throw new Error(
                  `toEntityGeometry: unhandled hatch edgeType ${String(exhaustive)}`,
                );
              }
            }
          }),
        })),
        // Pattern seed points (real 2D coords) — `.default([])`, always present (possibly empty).
        seedPoints: entity.seedPoints.map((p) => ({ x: p.x, y: p.y })),
        // The custom PAT pattern-definition lines — OPTIONAL (present only for a custom pattern).
        // Each line's `offset` is the STRUCTURED dash-row displacement {x,y}, NEVER flattened.
        ...(entity.customPatternLines !== undefined
          ? {
              customPatternLines: entity.customPatternLines.map((pl) => ({
                angle: pl.angle,
                base: { x: pl.base.x, y: pl.base.y },
                offset: { x: pl.offset.x, y: pl.offset.y },
                dashLengths: [...pl.dashLengths],
              })),
            }
          : {}),
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Block":
      return {
        kind: "Block",
        blockName: entity.blockName,
        insertionPoint: pointOptionalZ(entity.insertionPoint),
        scaleX: entity.scaleX,
        scaleY: entity.scaleY,
        scaleZ: entity.scaleZ,
        rotation: entity.rotation,
        columnCount: entity.columnCount,
        rowCount: entity.rowCount,
        columnSpacing: entity.columnSpacing,
        rowSpacing: entity.rowSpacing,
        extrusion: point3D(entity.extrusion),
        // ATTRIB children: the readable tag/value PLUS the per-ATTRIB draw-overrides (where / how the
        // attribute text is actually drawn) — each OPTIONAL, attached only when the ATTRIB overrides
        // the block default. The non-geometry overrideStyle / invisible / handle stay off this facet,
        // as does the INSERT's own `seqendHandle` (the terminating SEQEND's group-5 — round-trip
        // record identity on the typed model, not geometry).
        // @codex round-2 (round-17 local read-only follow-up) P2 — "Expose attribute handedness in
        // geometry snapshots": overrideGenerationFlags (group 71, mirror handedness) joins the other
        // three overrides — see `sceneGeometryFacet.ts`'s own doc for the full rationale.
        attributes: entity.attributes.map((a) => ({
          tag: a.tag,
          value: a.value,
          ...(a.overridePosition !== undefined
            ? { overridePosition: { x: a.overridePosition.x, y: a.overridePosition.y } }
            : {}),
          ...(a.overrideRotation !== undefined ? { overrideRotation: a.overrideRotation } : {}),
          ...(a.overrideHeight !== undefined ? { overrideHeight: a.overrideHeight } : {}),
          ...(a.overrideGenerationFlags !== undefined
            ? { overrideGenerationFlags: a.overrideGenerationFlags }
            : {}),
        })),
        // DYNAMIC-BLOCK evaluated state (finding :422) — the per-instance params + visibility the
        // preview/evaluator reads to materialize a dynamic INSERT's shape (blockName+transform alone
        // is not enough). `customProperties` is `.default({})` (always present); `visibilityState` +
        // `representationOverrides` are OPTIONAL (absent ⇒ inherit the block default). Only the
        // geometry-relevant divergence (`parameterOverrides` + `currentVisibilityStateId`) is lifted;
        // the opaque 102-XDICT linkage (etag / data handle) stays off this facet.
        customProperties: { ...entity.customProperties },
        ...(entity.visibilityState !== undefined
          ? { visibilityState: entity.visibilityState }
          : {}),
        ...(entity.representationOverrides !== undefined
          ? {
              representationOverrides: {
                parameterOverrides: { ...entity.representationOverrides.parameterOverrides },
                ...(entity.representationOverrides.currentVisibilityStateId !== undefined
                  ? {
                      currentVisibilityStateId:
                        entity.representationOverrides.currentVisibilityStateId,
                    }
                  : {}),
              },
            }
          : {}),
      };
    case "Rectangular Associative Array":
      // The defining GRID: base point + row/column/level grid + the 3D axis-direction vectors (the
      // real column/row axes vs the scalar axisAngle) + base-plane normal + per-item rotation. The
      // full AcDbAssocArray graph (evaluated item transforms / persisted item records / driving
      // expressions / handles) is the assoc-graph round-trip on the typed model, NOT the agent grid.
      return {
        kind: "Rectangular Associative Array",
        basePoint: pointOptionalZ(entity.basePoint),
        rowCount: entity.rowCount,
        columnCount: entity.columnCount,
        rowSpacing: entity.rowSpacing,
        columnSpacing: entity.columnSpacing,
        rowTotal: entity.rowTotal,
        columnTotal: entity.columnTotal,
        axisAngle: entity.axisAngle,
        associative: entity.associative,
        // The source-entity id list (finding :439) — the original pre-array entities whose geometry is
        // replicated across the grid (`.default([])`, always present). The evaluated/persisted assoc
        // graph stays off this facet (documented (c)-deferral).
        sourceEntityIds: [...entity.sourceEntityIds],
        ...(entity.levelCount !== undefined ? { levelCount: entity.levelCount } : {}),
        ...(entity.levelSpacing !== undefined ? { levelSpacing: entity.levelSpacing } : {}),
        ...(entity.levelTotal !== undefined ? { levelTotal: entity.levelTotal } : {}),
        ...(entity.rowElevation !== undefined ? { rowElevation: entity.rowElevation } : {}),
        ...(entity.xAxisDirection !== undefined
          ? { xAxisDirection: point3D(entity.xAxisDirection) }
          : {}),
        ...(entity.yAxisDirection !== undefined
          ? { yAxisDirection: point3D(entity.yAxisDirection) }
          : {}),
        ...(entity.basePlaneNormal !== undefined
          ? { basePlaneNormal: point3D(entity.basePlaneNormal) }
          : {}),
        ...(entity.itemRotation !== undefined ? { itemRotation: entity.itemRotation } : {}),
      };
    case "Viewport":
      return {
        kind: "Viewport",
        center: { x: entity.center.x, y: entity.center.y },
        width: entity.width,
        height: entity.height,
      };
    case "Polyline":
      return {
        kind: "Polyline",
        subtype: polylineSubtype(entity),
        flags: entity.flags,
        closed: polylineIsClosed(entity),
        vertices: entity.vertices.map((v) => ({
          handle: v.handle,
          location: point3D(v.location),
          ...(v.startWidth !== undefined ? { startWidth: v.startWidth } : {}),
          ...(v.endWidth !== undefined ? { endWidth: v.endWidth } : {}),
          ...(v.bulge !== undefined ? { bulge: v.bulge } : {}),
          ...(v.flags !== undefined ? { flags: v.flags } : {}),
          ...(v.curveFitTangent !== undefined ? { curveFitTangent: v.curveFitTangent } : {}),
          ...(v.faceVertexIndices !== undefined
            ? { faceVertexIndices: [...v.faceVertexIndices] }
            : {}),
          ...(v.vertexId !== undefined ? { vertexId: v.vertexId } : {}),
        })),
        elevation: point3D(entity.elevation),
        ...(entity.defaultStartWidth !== undefined
          ? { defaultStartWidth: entity.defaultStartWidth }
          : {}),
        ...(entity.defaultEndWidth !== undefined
          ? { defaultEndWidth: entity.defaultEndWidth }
          : {}),
        ...(entity.meshMCount !== undefined ? { meshMCount: entity.meshMCount } : {}),
        ...(entity.meshNCount !== undefined ? { meshNCount: entity.meshNCount } : {}),
        ...(entity.smoothSurfaceMDensity !== undefined
          ? { smoothSurfaceMDensity: entity.smoothSurfaceMDensity }
          : {}),
        ...(entity.smoothSurfaceNDensity !== undefined
          ? { smoothSurfaceNDensity: entity.smoothSurfaceNDensity }
          : {}),
        ...(entity.surfaceType !== undefined ? { surfaceType: entity.surfaceType } : {}),
        thickness: entity.thickness,
        ...(entity.extrusion !== undefined ? { extrusion: point3D(entity.extrusion) } : {}),
      };
    case "Table":
      // The per-cell records are deliberately NOT projected — see
      // `TableGeometry`'s doc: they are retained verbatim for round-trip and
      // have no typed model in this change, so an agent must not be handed a
      // structure it cannot act on.
      return {
        kind: "Table",
        blockName: entity.blockName,
        insertionPoint: point3D(entity.insertionPoint),
        ...(entity.horizontalDirection !== undefined
          ? { horizontalDirection: point3D(entity.horizontalDirection) }
          : {}),
        rotation: tableRotationDegrees(entity),
        ...(entity.rowCount !== undefined ? { rowCount: entity.rowCount } : {}),
        ...(entity.columnCount !== undefined ? { columnCount: entity.columnCount } : {}),
        rowHeights: [...entity.rowHeights],
        columnWidths: [...entity.columnWidths],
      };
    case "OLE Frame":
      // The embedded OLE payload is never decoded (see `oleFrameEntitySchema`),
      // so only its byte count is reported — never the bytes themselves.
      return {
        kind: "OLE Frame",
        upperLeft: point3D(entity.upperLeft),
        lowerRight: point3D(entity.lowerRight),
        ...(entity.objectDescription !== undefined
          ? { objectDescription: entity.objectDescription }
          : {}),
        ...(entity.oleObjectType !== undefined ? { oleObjectType: entity.oleObjectType } : {}),
        ...(entity.drawAspect !== undefined ? { drawAspect: entity.drawAspect } : {}),
        ...(entity.binaryByteCount !== undefined
          ? { binaryByteCount: entity.binaryByteCount }
          : {}),
      };
    default: {
      const exhaustive: never = entity;
      throw new Error(
        `toEntityGeometry: unhandled entity type ${String((exhaustive as { type?: string }).type)}`,
      );
    }
  }
}

/**
 * The TOP-LEVEL element count of every array-valued field on a projected geometry object (the
 * heavy defining arrays — a polyline's `vertices`, a spline's `controlPoints`/`knots`/`weights`/
 * `fitPoints`, a hatch's `boundaryPaths`/`seedPoints`, a multileader's `leaderRoots`, …). Used by
 * `capGeometry` to report WHICH arrays (and how large) were dropped on an oversized entity, so the
 * agent sees the magnitude withheld. Counts the immediate array length only (a nested array — a
 * hatch path's edges — is summarized by its parent's length; the agent re-queries narrower).
 */
function droppedArrayCounts(geometry: SnapshotEntityGeometry): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const [key, value] of Object.entries(geometry)) {
    if (Array.isArray(value)) {
      counts[key] = value.length;
    }
  }
  return counts;
}

/**
 * Cap a single entity's projected geometry by serialized SIZE (@codex T1 round-2 P2 — finding :1411).
 * `?fullField=geometry` is a single-item (limit=1) UNCAPPED read; a colossal entity would serialize
 * past the companion's per-frame budget (`readBudget.ts` `WS_MAX_PAYLOAD_BYTES`) and HARD-REJECT the
 * page — unrecoverably, since limit is already 1. So when `JSON.stringify(geometry)` exceeds
 * `GEOMETRY_PER_ENTITY_BYTE_CAP` we emit a `TruncatedGeometry` marker (the entity `type`, the byte
 * size, the cap, the dropped arrays' element counts, a narrower-query hint) INSTEAD of the heavy
 * shape. The read then never hard-rejects; the agent gets a bounded, usable signal. The common case
 * (every normal entity) is UNDER the cap → the full geometry passes through verbatim, untouched.
 */
function capGeometry(geometry: SnapshotEntityGeometry): SnapshotEntityGeometry {
  const serialized = JSON.stringify(geometry);
  // `JSON.stringify` returns a JS string; its BYTE size (UTF-8) is what the companion budgets. Each
  // code unit is ≤4 bytes; an ASCII-dominant numeric geometry payload is ~1 byte/char, so the char
  // length is a safe LOWER bound — but to match the companion's byte budget exactly, measure UTF-8.
  const byteSize = utf8ByteLength(serialized);
  if (byteSize <= GEOMETRY_PER_ENTITY_BYTE_CAP) {
    return geometry; // under cap — pass the full geometry through verbatim (the common case).
  }
  return {
    kind: "Truncated",
    type: geometry.kind,
    byteSize,
    cap: GEOMETRY_PER_ENTITY_BYTE_CAP,
    truncated: true,
    droppedArrayCounts: droppedArrayCounts(geometry),
    narrowerQuery:
      "geometry exceeds the per-entity byte cap; re-read this entity's defining arrays in narrower slices",
  };
}

/**
 * UTF-8 byte length of a string WITHOUT allocating a Buffer (this is a browser/worker leaf — no
 * node `Buffer`). `TextEncoder` is available in every browser + worker + the vitest jsdom/node env.
 * One shared encoder instance (encoders are stateless + reusable).
 */
const GEOMETRY_BYTE_ENCODER = new TextEncoder();
function utf8ByteLength(s: string): number {
  return GEOMETRY_BYTE_ENCODER.encode(s).length;
}

/**
 * Project one typed `DrawingEntity` onto its `SnapshotEntityGeometry` arm (verbatim, full fidelity),
 * then SIZE-CAP it (`capGeometry`) so a colossal single entity never hard-rejects a `?fullField`
 * read (finding :1411). The PUBLIC entry point used by `captureSnapshot`'s lazy geometry getter.
 */
export function toEntityGeometry(entity: DrawingEntity): SnapshotEntityGeometry {
  return capGeometry(projectGeometry(entity));
}

/**
 * Project one typed `DrawingEntity` onto its `SnapshotEntityGeometry` arm — UNCAPPED (no
 * `capGeometry` byte-cap). The cap exists to protect the `entities` route's `?fullField=
 * geometry` WIRE payload (a colossal single-entity retrieval could exceed the companion's
 * per-frame budget when serialized); it is NOT relevant to an INTERNAL consumer that never
 * re-serializes the raw geometry over the wire (e.g. `ai/sceneQuery/osnapQuery.ts`, whose
 * output is a small `{mode,point,ownerEntityId,distanceWorld}` — the geometry itself never
 * reaches the agent). Exported for that internal use ONLY — the `entities` route's public
 * `geometry`/`?fullField=geometry` projection MUST keep going through the capped
 * `toEntityGeometry` (do not swap the route body to this).
 */
export function toEntityGeometryUncapped(entity: DrawingEntity): SnapshotEntityGeometry {
  return projectGeometry(entity);
}
